import { useState } from "react";
import { ChevronDown, Phone, HelpCircle, MessageCircle, Clock, MapPin, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Faq = () => {
  const [openItems, setOpenItems] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const faqData = {
    booking: {
      title: "Booking & Scheduling",
      icon: "📅",
      questions: [
        { question: "How do I book a bike service?", answer: "Booking is simple! Just visit our website or app, select your bike brand and model, choose the service type, pick a convenient date and time slot, and confirm your booking. You can also call us at +91 9923887090 for assistance. We offer doorstep pickup service across Pune." },
        { question: "Can I schedule a service for the same day?", answer: "Yes, we offer same-day service bookings subject to slot availability. For urgent services, please call our support team at +91 9923887090. We recommend booking at least 24 hours in advance to secure your preferred time slot." },
        { question: "How can I track my service status?", answer: "Once your booking is confirmed, you can track your service status in real-time through our app or website. You'll receive SMS and WhatsApp updates at every stage - pickup, diagnosis, work in progress, quality check, and delivery." },
        { question: "Can I reschedule my service appointment?", answer: "Yes, you can reschedule your appointment free of charge if done at least 6 hours before the scheduled pickup time. To reschedule, visit your booking details in the app/website or call us at +91 9923887090." },
        { question: "What areas do you cover in Pune?", answer: "We provide free doorstep pickup and delivery across all major areas in Pune including Kothrud, Baner, Hinjewadi, Kharadi, Viman Nagar, Hadapsar, Wakad, Aundh, Shivajinagar, Koregaon Park, Pimpri, and Chinchwad." },
      ],
    },
    payment: {
      title: "Payment & Pricing",
      icon: "💳",
      questions: [
        { question: "What payment methods do you accept?", answer: "We accept multiple payment methods including UPI (Google Pay, PhonePe, Paytm), credit/debit cards, net banking, and cash on delivery. You can pay after the service is completed and you're satisfied with the work." },
        { question: "Do I need to pay in advance?", answer: "No advance payment is required for most services. You only pay after your bike is serviced and delivered back to you. Final billing is transparent with a detailed breakup of parts and labor costs." },
        { question: "Are there any hidden charges?", answer: "Absolutely not! We believe in 100% transparent pricing. The price you see during booking is what you pay. Any additional work or part replacements will only be done after your explicit approval." },
        { question: "Do you provide service estimates before starting work?", answer: "Yes, after our technician inspects your bike, you'll receive a detailed service estimate including parts cost, labor charges, and any additional recommendations. Work begins only after your approval." },
        { question: "What is your cancellation policy?", answer: "You can cancel your booking free of charge up to 6 hours before the scheduled pickup time. For cancellations made less than 6 hours before pickup, a nominal cancellation fee of ₹100 may apply." },
      ],
    },
    service: {
      title: "Service & Repairs",
      icon: "🔧",
      questions: [
        { question: "What types of services do you offer?", answer: "We offer comprehensive bike services including General Service (oil change, filter replacement, tune-up), Repairs (engine, electrical, mechanical issues), Spare Parts replacement, Washing & Detailing, Custom modifications, and Annual Maintenance Contracts." },
        { question: "How long does a typical service take?", answer: "General servicing typically takes 4-6 hours. Complex repairs may take 1-2 days depending on parts availability and work complexity. We provide accurate time estimates after initial inspection." },
        { question: "Do you use genuine spare parts?", answer: "Yes, we use only 100% genuine OEM (Original Equipment Manufacturer) parts sourced directly from authorized dealers. All parts come with manufacturer warranty." },
        { question: "Do you provide warranty on services?", answer: "Yes! All our services come with a 6-month/3000 km warranty (whichever comes first). Spare parts carry manufacturer warranty ranging from 6 months to 2 years." },
        { question: "Are your mechanics certified?", answer: "All our mechanics are trained and certified professionals with years of experience. They undergo regular training programs on latest bike technologies and repair techniques." },
        { question: "Can you handle emergency breakdowns?", answer: "Yes, we offer emergency breakdown assistance. Call our helpline at +91 9923887090 and we'll arrange immediate pickup. We prioritize emergency cases." },
      ],
    },
    general: {
      title: "General Information",
      icon: "ℹ️",
      questions: [
        { question: "What documents do I need for service?", answer: "You need to provide your bike's Registration Certificate (RC) copy and a valid ID proof (Aadhaar, PAN, Driving License). These are required for documentation and insurance purposes." },
        { question: "Do you offer doorstep pickup and delivery?", answer: "Yes! Free doorstep pickup and delivery is included in all our services across Pune. Our rider will come to your location at the scheduled time, pick up your bike, and deliver it back after servicing." },
        { question: "What if I'm not satisfied with the service?", answer: "Customer satisfaction is our top priority. If you're not happy with our service, contact us immediately at +91 9923887090. We'll re-inspect and rectify any issues free of charge." },
        { question: "Do you service electric bikes/scooters?", answer: "Yes, we service electric two-wheelers including brands like Ather, Ola Electric, TVS iQube, Bajaj Chetak, and others. Our technicians are trained in EV technology." },
        { question: "How do I contact customer support?", answer: "You can reach our customer support team via Phone: +91 9923887090 (9 AM - 8 PM, Mon-Sun), Email: okmechanics@gmail.com, WhatsApp: +91 9923887090, or through Live Chat on our website." },
      ],
    },
  };

  const getFilteredData = () => {
    if (!searchTerm) return faqData;
    const filtered = {};
    Object.entries(faqData).forEach(([key, category]) => {
      const filteredQuestions = category.questions.filter(
        (q) =>
          q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredQuestions.length > 0) {
        filtered[key] = { ...category, questions: filteredQuestions };
      }
    });
    return filtered;
  };

  const filteredData = getFilteredData();

  const stats = [
    { value: "24/7", label: "Support Available" },
    { value: "5000+", label: "Happy Customers" },
    { value: "100%", label: "Genuine Parts" },
    { value: "6 Mo", label: "Service Warranty" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <div className="relative bg-[#111111] border-b border-white/[0.08] py-16 sm:py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`, backgroundSize: "50px 50px" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[120px] opacity-5 pointer-events-none" />

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-[#FF6B35]" />
            </div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">
              Help Center
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-base sm:text-lg text-gray-400 mb-8">
              Find quick answers about OkMechanic bike servicing
            </p>

            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder="Search for questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-5 py-4 bg-white/[0.06] border border-white/[0.10] text-white rounded-2xl focus:border-[#FF6B35]/50 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]/20 transition-all text-base placeholder-gray-600"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl p-4 text-center hover:border-[#FF6B35]/20 transition-colors"
            >
              <div className="text-xl sm:text-2xl font-black text-[#FF6B35] mb-1">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ Categories */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        {Object.keys(filteredData).length === 0 ? (
          <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-12 text-center">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-white mb-2">No results found</h3>
            <p className="text-gray-500">Try searching with different keywords</p>
          </div>
        ) : (
          Object.entries(filteredData).map(([key, category], catIdx) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: catIdx * 0.1 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4 px-1">
                <div className="w-10 h-10 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center text-xl">
                  {category.icon}
                </div>
                <div>
                  <h2 className="text-lg font-black text-white">{category.title}</h2>
                  <p className="text-xs text-gray-500">{category.questions.length} question{category.questions.length !== 1 ? "s" : ""}</p>
                </div>
              </div>

              <div className="space-y-2">
                {category.questions.map((item, index) => {
                  const itemKey = `${key}-${index}`;
                  const isOpen = openItems[itemKey];
                  return (
                    <div key={index} className={`bg-[#111111] border rounded-xl overflow-hidden transition-colors ${isOpen ? "border-[#FF6B35]/30" : "border-white/[0.08] hover:border-white/[0.12]"}`}>
                      <button
                        onClick={() => toggleItem(key, index)}
                        className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors"
                      >
                        <span className="font-semibold text-white text-sm sm:text-base leading-snug">
                          {item.question}
                        </span>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex-shrink-0"
                        >
                          <ChevronDown className={`w-5 h-5 ${isOpen ? "text-[#FF6B35]" : "text-gray-500"}`} />
                        </motion.div>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                          >
                            <div className="px-5 pb-5 text-gray-400 leading-relaxed border-t border-white/[0.06] pt-4 text-sm">
                              {item.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Quick Links */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-[#111111] border border-white/[0.08] hover:border-[#FF6B35]/20 rounded-2xl p-5 transition-colors">
            <div className="w-10 h-10 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center mb-3">
              <MapPin className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <h3 className="font-bold text-white mb-1">Visit Us</h3>
            <p className="text-sm text-gray-500 leading-relaxed">6, Gen Vaidya Marg Agarkar Nagar Pune, Maharashtra 411001 India</p>
          </div>
          <div className="bg-[#111111] border border-white/[0.08] hover:border-[#FF6B35]/20 rounded-2xl p-5 transition-colors">
            <div className="w-10 h-10 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center mb-3">
              <Phone className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <h3 className="font-bold text-white mb-1">Call Us</h3>
            <p className="text-sm text-gray-500 mb-1">Customer Support:</p>
            <a href="tel:+919923887090" className="text-[#FF6B35] font-semibold hover:underline">+91 9923887090</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;
